<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import HtmlEmailEditor from './HtmlEmailEditor.vue';

const props = defineProps({
  notification: { type: Object, default: null },
  inboxes: { type: Array, default: () => [] },
});

const emit = defineEmits(['close']);

const store = useStore();
const getters = useStoreGetters();

const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const contactLists = computed(
  () => getters['contactLists/getContactLists'].value || []
);

const isEditing = computed(() => !!props.notification);

const initListIds = () => {
  if (props.notification?.contact_list_ids?.length) {
    return [...props.notification.contact_list_ids];
  }
  if (props.notification?.contact_list_id) {
    return [props.notification.contact_list_id];
  }
  return [];
};

const emailConfigs = computed(
  () => getters['notificationEmailConfigs/getEmailConfigs'].value || []
);

const state = reactive({
  title: props.notification?.title || '',
  inboxId: props.notification?.inbox_id || '',
  templateId: '',
  selectedListIds: initListIds(),
  scheduledAt: '',
  sendInterval: props.notification?.send_interval || 5,
  imageUrl: props.notification?.image_url || '',
  sendEmail: props.notification?.send_email || false,
  emailSubject: props.notification?.email_subject || '',
  emailBody: props.notification?.email_body || '',
  emailConfigId: props.notification?.notification_email_config_id || '',
});

const toggleList = listId => {
  const idx = state.selectedListIds.indexOf(listId);
  if (idx >= 0) {
    state.selectedListIds.splice(idx, 1);
  } else {
    state.selectedListIds.push(listId);
  }
};

const selectedContactsTotal = computed(() => {
  return contactLists.value
    .filter(l => state.selectedListIds.includes(l.id))
    .reduce((sum, l) => sum + (l.contacts_count || 0), 0);
});

const templateParserRef = ref(null);

onMounted(() => {
  store.dispatch('contactLists/get');
  store.dispatch('notificationEmailConfigs/get');

  if (props.notification?.scheduled_at) {
    const d = new Date(props.notification.scheduled_at);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    state.scheduledAt = local.toISOString().slice(0, 16);
  }
});

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = getFilteredWhatsAppTemplates.value(state.inboxId);
  return (templates || []).map(template => {
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(o => o.value === state.templateId)
    ?.template;
});

watch(
  () => state.inboxId,
  () => {
    state.templateId = '';
  }
);

const isValid = computed(() => {
  return (
    state.title.trim() &&
    state.inboxId &&
    state.selectedListIds.length > 0 &&
    state.scheduledAt
  );
});

const handleSubmit = async () => {
  if (!isValid.value) return;

  const scheduledAtUTC = new Date(state.scheduledAt).toISOString();

  const templateData = {};
  const currentTemplate = selectedTemplate.value;
  if (currentTemplate && templateParserRef.value) {
    const parserData = templateParserRef.value;
    templateData.name = currentTemplate.name;
    templateData.language = currentTemplate.language;
    templateData.namespace = currentTemplate.namespace;
    if (parserData.processedParams) {
      templateData.processed_params = parserData.processedParams;
    }
  }

  const messageContent =
    currentTemplate?.components?.find(c => c.type === 'BODY')?.text || '';

  const data = {
    title: state.title,
    inbox_id: state.inboxId,
    contact_list_ids: state.selectedListIds,
    scheduled_at: scheduledAtUTC,
    send_interval: state.sendInterval,
    image_url: state.imageUrl,
    message: messageContent,
    template_params: Object.keys(templateData).length > 0 ? templateData : {},
    send_email: state.sendEmail,
    email_subject: state.sendEmail ? state.emailSubject : null,
    email_body: state.sendEmail ? state.emailBody : null,
    notification_email_config_id: state.sendEmail ? state.emailConfigId : null,
  };

  try {
    if (isEditing.value) {
      await store.dispatch('scheduledNotifications/update', {
        id: props.notification.id,
        ...data,
      });
      useAlert('Notificação atualizada com sucesso');
    } else {
      await store.dispatch('scheduledNotifications/create', data);
      useAlert('Notificação agendada com sucesso');
    }
    emit('close');
  } catch (error) {
    useAlert('Erro ao salvar notificação');
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black5"
    @click.self="emit('close')"
  >
    <div
      class="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-n-surface-1 rounded-xl shadow-xl p-6"
    >
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-xl font-bold text-n-slate-12">
          {{ isEditing ? 'Editar Notificação' : 'Nova Notificação' }}
        </h2>
        <button
          class="p-1 rounded hover:bg-n-alpha-2"
          @click="emit('close')"
        >
          <div class="i-lucide-x w-5 h-5 text-n-slate-11" />
        </button>
      </div>

      <div class="flex flex-col gap-4">
        <!-- Título -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">Título *</label>
          <input
            v-model="state.title"
            type="text"
            class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
            placeholder="Nome da notificação"
          />
        </div>

        <!-- Inbox -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">
            Inbox WhatsApp *
          </label>
          <select
            v-model="state.inboxId"
            class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
          >
            <option value="">Selecione uma inbox</option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
        </div>

        <!-- Template -->
        <div v-if="state.inboxId" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">
            Template WhatsApp
          </label>
          <select
            v-model="state.templateId"
            class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
          >
            <option value="">Selecione um template</option>
            <option
              v-for="opt in templateOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>

        <!-- Template Parser -->
        <WhatsAppTemplateParser
          v-if="selectedTemplate"
          ref="templateParserRef"
          :template="selectedTemplate"
        />

        <!-- Listas de Contatos -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">
            Listas de Contatos *
          </label>
          <div
            class="max-h-40 overflow-y-auto border rounded-lg border-n-strong p-2 flex flex-col gap-1"
          >
            <label
              v-for="list in contactLists"
              :key="list.id"
              class="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-n-alpha-2 text-sm text-n-slate-12"
            >
              <input
                type="checkbox"
                :checked="state.selectedListIds.includes(list.id)"
                class="accent-n-brand"
                @change="toggleList(list.id)"
              />
              {{ list.name }}
              <span class="text-xs text-n-slate-10">
                ({{ list.contacts_count }} contatos)
              </span>
            </label>
            <p
              v-if="contactLists.length === 0"
              class="text-xs text-n-slate-10 px-2 py-1"
            >
              Nenhuma lista cadastrada
            </p>
          </div>
          <p
            v-if="state.selectedListIds.length > 0"
            class="text-xs text-n-slate-10"
          >
            {{ state.selectedListIds.length }} lista(s) selecionada(s) —
            {{ selectedContactsTotal }} contatos no total
          </p>
        </div>

        <!-- Data/Hora -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">
            Data e Hora do Envio *
          </label>
          <input
            v-model="state.scheduledAt"
            type="datetime-local"
            class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
          />
        </div>

        <!-- Intervalo -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">
            Intervalo entre envios (segundos)
          </label>
          <input
            v-model.number="state.sendInterval"
            type="number"
            min="1"
            max="60"
            class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
          />
        </div>

        <!-- Image URL -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-11">
            URL da Imagem (opcional)
          </label>
          <input
            v-model="state.imageUrl"
            type="url"
            class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
            placeholder="https://..."
          />
        </div>

        <!-- Email Section -->
        <div class="border-t border-n-strong pt-4 mt-2">
          <label
            class="flex items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer mb-4"
          >
            <input
              v-model="state.sendEmail"
              type="checkbox"
              class="accent-n-brand"
            />
            Enviar e-mail junto com a notificação
          </label>

          <div v-if="state.sendEmail" class="flex flex-col gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Configuração SMTP *
              </label>
              <select
                v-model="state.emailConfigId"
                class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
              >
                <option value="">Selecione</option>
                <option
                  v-for="cfg in emailConfigs"
                  :key="cfg.id"
                  :value="cfg.id"
                >
                  {{ cfg.name }} ({{ cfg.from_email }})
                </option>
              </select>
              <p v-if="emailConfigs.length === 0" class="text-xs text-n-ruby-11">
                Nenhuma configuração SMTP cadastrada. Acesse "Config E-mail" no
                menu.
              </p>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Assunto do E-mail *
              </label>
              <input
                v-model="state.emailSubject"
                type="text"
                placeholder="Ex: Nova circular disponível"
                class="px-3 py-2 text-sm border rounded-lg border-n-strong bg-n-surface-1 text-n-slate-12"
              />
              <p
                class="text-xs text-n-slate-10"
                v-text="'Variáveis: {{nome}}, {{empresa}}, {{cumprimento}}'"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                Corpo do E-mail *
              </label>
              <HtmlEmailEditor v-model="state.emailBody" />
              <p
                class="text-xs text-n-slate-10"
                v-text="'Variáveis: {{cumprimento}}, {{nome}}, {{name}}, {{email}}, {{phone}}, {{empresa}}'"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-3 mt-6">
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
          @click="emit('close')"
        >
          Cancelar
        </button>
        <button
          :disabled="!isValid"
          class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
          @click="handleSubmit"
        >
          {{ isEditing ? 'Salvar' : 'Agendar' }}
        </button>
      </div>
    </div>
  </div>
</template>
